import { linkLabel } from '../lib/labels';
import type { ConditionRowView } from '../lib/api';

const FAILURE_CLASS_BY_CONDITION: Record<string, string> = {
  'dead-letter-misfiled': 'misfiled',
  'dead-letter-unreadable': 'poison',
  'dead-letter-collision': 'collision',
  'dead-letter-unroutable': 'unroutable',
  'dead-letter-internal': 'internal',
};

/**
 * One work-list row: a headline, a consequence, and its control — never a bare number the
 * operator has to judge. A disabled control still shows its
 * label and `note`, never omitted silently (this row's own acceptance bar).
 *
 * **`onSendToReview` (OPS-04)**: `human-review-drain` and `spot-check-drain` both carry
 * *Send to review*, "the same control, different source" — this row does not decide the source
 * itself, it asks the parent to open the shared guard panel with the right one.
 *
 * Every row gets the same treatment — a marker, a tint, and a hairline all round. It is not a
 * missing feature: `ConditionRowView` carries no severity, because a condition is either firing
 * (it needs a human) or it is absent from the list. Ranking them here would be this component
 * inventing a judgement the backend deliberately does not make.
 * ponytail: one severity band; add `--crit` when `ConditionRowView` gains a real severity field.
 */
export function ConditionRow({
  row,
  onSendToReview,
}: {
  row: ConditionRowView;
  onSendToReview: (source: 'escalation' | 'spot-check') => void;
}) {
  return (
    <li className="wrow wrow--warn">
      {/* QA-93: the design's warning icon. The headline carries the meaning, so it is hidden from
          assistive technology. */}
      <svg className="status-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path fillRule="evenodd" d="M12 2 1 21h22L12 2Zm-1 7h2v6h-2V9Zm0 8h2v2h-2v-2Z" />
      </svg>
      <p className="wrow__headline">{row.headline}</p>
      <p className="wrow__detail">{row.detail}</p>
      <div className="wrow__controls end-cell">
        <RowControl row={row} onSendToReview={onSendToReview} />
      </div>
    </li>
  );
}

/** `everything-paused` has no row here: the Pipeline status card draws it (QA-117).
 *  `human-review-drain`/`spot-check-drain` open the shared *Send to review* guard panel instead
 *  of acting immediately, since that write needs a selection first.
 *
 *  The control's `kind` picks its voice (`design.md` § CTA voice): `guarded` writes wear the warn
 *  fill so the operator can see which controls ask before they click one; a `one-click` write is
 *  quiet; a `link` is navigation and is dressed as text. */
function RowControl({
  row,
  onSendToReview,
}: {
  row: ConditionRowView;
  onSendToReview: (source: 'escalation' | 'spot-check') => void;
}) {
  const { control } = row;

  if (control.kind === 'link' && control.href) {
    return (
      <a className="btn wrow__link" href={contextualLink(row.id, control.href)}>
        {linkLabel(control.label)}
      </a>
    );
  }

  function handleClick() {
    if (control.disabled) return;
    if (row.id === 'human-review-drain') {
      onSendToReview('escalation');
    } else if (row.id === 'spot-check-drain') {
      onSendToReview('spot-check');
    } else if (row.id === 'review-batch-stuck') {
      // QA-161: the panel of the first stuck source; `residual` is the `escalation` panel.
      onSendToReview(row.stuckSources?.[0] === 'spot-check' ? 'spot-check' : 'escalation');
    }
  }

  return (
    <span className="action-row">
      <button
        type="button"
        className={control.kind === 'guarded' ? 'btn btn--guard' : 'btn'}
        disabled={control.disabled}
        title={control.note}
        aria-label={`${control.label}${control.note ? ` — ${control.note}` : ''}`}
        onClick={handleClick}
      >
        {control.label}
      </button>
      {control.note && <span className="action-row__note">{control.note}</span>}
      {control.secondaryLink && (
        <a href={contextualLink(row.id, control.secondaryLink.href)}>{linkLabel(control.secondaryLink.label)}</a>
      )}
    </span>
  );
}

function contextualLink(conditionId: string, href: string): string {
  const failureClass = FAILURE_CLASS_BY_CONDITION[conditionId];
  if (!failureClass || href !== '#not-processed') return href;
  return `${href}?failureClass=${encodeURIComponent(failureClass)}`;
}
